import { createSlice } from "@reduxjs/toolkit";

import type {
  WalletAsyncStatus,
  WalletDetailsData,
  WalletMovementRecord,
  WalletSummaryData,
} from "@repo/ui/types/wallet";

import { useAppSelector } from "../hooks";
import {
  fetchRefundRedirectToken,
  fetchWalletDetails,
  fetchWalletHistory,
  fetchWalletSummary,
  fetchWithdrawalEligibleVouchers,
} from "../thunks";

export interface WalletState {
  summary: WalletSummaryData | null;
  summaryStatus: WalletAsyncStatus;
  summaryError: string | null;
  data: WalletDetailsData | null;
  status: WalletAsyncStatus;
  error: string | null;
  history: WalletMovementRecord[] | null;
  historyStatus: WalletAsyncStatus;
  historyError: string | null;
  withdrawalVouchers: WalletDetailsData | null;
  withdrawalVouchersStatus: WalletAsyncStatus;
  withdrawalVouchersError: string | null;
  refundRedirectStatus: WalletAsyncStatus;
  refundRedirectError: string | null;
}

const initialState: WalletState = {
  summary: null,
  summaryStatus: "idle",
  summaryError: null,
  data: null,
  status: "idle",
  error: null,
  history: null,
  historyStatus: "idle",
  historyError: null,
  withdrawalVouchers: null,
  withdrawalVouchersStatus: "idle",
  withdrawalVouchersError: null,
  refundRedirectStatus: "idle",
  refundRedirectError: null,
};

const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    resetWallet: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWalletSummary.pending, (state) => {
        state.summaryStatus = "loading";
        state.summaryError = null;
      })
      .addCase(fetchWalletSummary.fulfilled, (state, action) => {
        state.summaryStatus = "success";
        state.summary = action.payload;
        state.summaryError = null;
      })
      .addCase(fetchWalletSummary.rejected, (state, action) => {
        state.summaryStatus = "error";
        state.summaryError = action.payload ?? "Unknown error";
        state.summary = null;
      })
      .addCase(fetchWalletDetails.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchWalletDetails.fulfilled, (state, action) => {
        state.status = "success";
        state.data = action.payload;
        state.error = null;
        if (!action.payload.customerNumber) {
          state.historyStatus = "error";
          state.historyError = "No customer number returned — history unavailable";
        }
      })
      .addCase(fetchWalletDetails.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload ?? "Unknown error";
        state.data = null;
        state.historyStatus = "error";
        state.historyError = "Cannot load history: wallet details failed to load";
      })
      .addCase(fetchWalletHistory.pending, (state) => {
        state.historyStatus = "loading";
        state.historyError = null;
      })
      .addCase(fetchWalletHistory.fulfilled, (state, action) => {
        state.historyStatus = "success";
        state.history = action.payload;
        state.historyError = null;
      })
      .addCase(fetchWalletHistory.rejected, (state, action) => {
        state.historyStatus = "error";
        state.historyError = action.payload ?? "Unknown error";
        state.history = null;
      })
      .addCase(fetchWithdrawalEligibleVouchers.pending, (state) => {
        state.withdrawalVouchersStatus = "loading";
        state.withdrawalVouchersError = null;
      })
      .addCase(fetchWithdrawalEligibleVouchers.fulfilled, (state, action) => {
        state.withdrawalVouchersStatus = "success";
        state.withdrawalVouchers = action.payload;
        state.withdrawalVouchersError = null;
      })
      .addCase(fetchWithdrawalEligibleVouchers.rejected, (state, action) => {
        state.withdrawalVouchersStatus = "error";
        state.withdrawalVouchersError = action.payload ?? "Unknown error";
        state.withdrawalVouchers = null;
      })
      .addCase(fetchRefundRedirectToken.pending, (state) => {
        state.refundRedirectStatus = "loading";
        state.refundRedirectError = null;
      })
      .addCase(fetchRefundRedirectToken.fulfilled, (state) => {
        state.refundRedirectStatus = "success";
        state.refundRedirectError = null;
      })
      .addCase(fetchRefundRedirectToken.rejected, (state, action) => {
        state.refundRedirectStatus = "error";
        state.refundRedirectError = action.payload ?? "Unknown error";
      });
  },
});

export const { resetWallet } = walletSlice.actions;
export default walletSlice.reducer;

// Not persisted — financial data must always come from the live BFF response.
export const useWalletState = () => useAppSelector((state) => state.wallet);
